import { createEventQueue, type EventQueue } from './event-queue';
import { Uploader, type UploadStats } from './uploader';

/**
 * Application-level upload singleton.
 *
 * Why a singleton: the queue lives in IndexedDB so it must persist
 * across pages, sessions, and offline drives. The uploader needs to
 * run whenever the app is open — not just while RecordPage is on
 * screen — otherwise queued events can sit around forever if the
 * user records a trip then leaves the app on a different page
 * (or closes and reopens it later).
 *
 * The Uploader itself already reacts to `online` and `visibilitychange`,
 * so this singleton mainly handles the lifecycle and stats fan-out
 * to any subscribed React components.
 */

let queue: EventQueue | null = null;
let uploader: Uploader | null = null;
let latestStats: UploadStats = {
  uploaded: 0,
  pending: 0,
  lastError: null,
  lastSuccessTs: null,
};
const listeners = new Set<(s: UploadStats) => void>();

export function getQueue(): EventQueue {
  if (!queue) queue = createEventQueue();
  return queue;
}

export function startAmbientUploader(): void {
  if (uploader) return;
  uploader = new Uploader({
    queue: getQueue(),
    intervalMs: 60_000, // background cadence — recording boosts it via flush()
    onStats: (s) => {
      latestStats = s;
      listeners.forEach((l) => l(s));
    },
  });
  uploader.start();
}

/** Subscribe to upload stats; receives current snapshot immediately + on every change. */
export function subscribeStats(cb: (s: UploadStats) => void): () => void {
  listeners.add(cb);
  cb(latestStats);
  return () => listeners.delete(cb);
}

/** Trigger an upload now. Safe to call repeatedly — the uploader serialises in-flight batches. */
export function flushNow(): Promise<void> {
  return uploader?.flush() ?? Promise.resolve();
}

export function currentStats(): UploadStats {
  return latestStats;
}
